import type {
  ElementRef,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import {
  ViewChild,
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
} from '@angular/core';
import type { TBookAlias } from '@shared/jp/product';
import { TBookCover } from '@shared/jp/product';
import { Logger } from '@shared/common/logger';
import {
  BehaviorSubject,
  combineLatest,
  firstValueFrom,
  Subject,
  shareReplay,
  takeUntil,
  of,
} from 'rxjs';
import { environment } from 'src/environments/environment';
import { ELoaderType, LoaderService } from '@studiobuki/web-core/lib/loader';
import {
  getPaymentInformation,
  scrollToElement,
  isParamChanged,
} from 'src/utils';
import {
  EFormShipFromValue,
  EGiftOrderStatus,
  EGiftOrderType,
} from '@shared/common/gift';
import type {
  TGiftOrderCreateParams,
  TGiftOrderCreated,
  IGiftOrderCreateParamsCommon,
  TGiftOrderCreateParamsError,
} from '@shared/jp/gift';
import cookie from 'cookie';
import { StripeService } from '@studiobuki/web-core/lib/stripe';
import { checkGeneratedGiftOrder, checkGiftOrderError } from '@shared/jp/gift';
import { distinctUntilChanged, filter, map, switchMap } from 'rxjs/operators';
import { RoutingService } from 'src/app/services/routing.service';
import { deleteUndefinedFromObject, getError } from '@shared/common/utils';
import { IShipping } from '@shared/jp/data/shipping';
import type {
  IAddress,
  IAddressShippingData,
  IBookSimpleData,
} from '@shared/jp/interfaces';
import { EFormPaymentType } from '@shared/jp/interfaces';
import { FirebaseService } from 'src/app/services/firebase.service';
import { DialogsService } from '@studiobuki/web-core/lib/dialogs';
import type { IEmail } from '@studiobuki/web-core/lib/form-email';
import type { IFormGiftSpecialData } from '@studiobuki/web-core/lib/form-gift-special';
import type { TRegionNameEnglish } from '@shared/jp/shipping/interfaces';
import { REGION_NAME_ENGLISH_DEFAULT } from '@shared/jp/shipping/constants';
import { getRegion } from '@shared/jp/shipping/utils';
import { ActivatedRoute } from '@angular/router';
import type { TWithDocId } from '@shared/common/interfaces';
import { ROUTE_PATH_PARAM_ORDER_ID } from '@studiobuki/web-core/lib/routing';
import { sendConversionEvent } from 'src/analytics';
import { BOOK_ALIAS_GIFT } from '@shared/common/product';
import Subscriber from '@shared/common/subscriber';
import { DiscountService } from 'src/app/services/discount.service';
import { calculateDiscount } from '@shared/common/discount/utils';

const log = new Logger('SectionGiftCheckoutComponent');

@Component({
  selector: 'app-section-gift-checkout',
  templateUrl: './section-gift-checkout.component.html',
  styleUrls: ['./section-gift-checkout.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SectionGiftCheckoutComponent
  implements OnInit, OnChanges, OnDestroy
{
  @ViewChild('wrapRef') wrapRef: ElementRef<HTMLDivElement> | undefined;

  private _type$ = new BehaviorSubject<EGiftOrderType>(EGiftOrderType.standart);

  @Input() set type(value: EGiftOrderType) {
    this._type$.next(value);
  }

  get type(): EGiftOrderType {
    return this._type$.value;
  }

  @Input() alias?: TBookAlias | undefined;

  @Input() cover!: TBookCover;

  @Input() shipping!: IShipping;

  private readonly _destroy$ = new Subject<void>();

  public readonly environment = environment;

  public readonly parseStateFn = (name: any) => getRegion(name).nameEnglish;

  /** used to touch forms to make errors appear */
  public readonly touchEventEmitter = new EventEmitter<void>();

  public readonly formGiftSpecialData$ = new BehaviorSubject<
    IFormGiftSpecialData | undefined | null
  >(undefined);

  public restrictedAliasArr?: TBookAlias[] | undefined;

  public readonly formEmailData$ = new BehaviorSubject<
    IEmail | undefined | null
  >(undefined);

  public readonly formBillingAddressData$ = new BehaviorSubject<
    IAddress | undefined | null
  >(undefined);

  public readonly shipFrom$ = new BehaviorSubject<EFormShipFromValue>(
    EFormShipFromValue.buki,
  );

  public readonly formPaymentType$ = new BehaviorSubject(EFormPaymentType.card);

  public readonly submitButtonDisabled$ = new BehaviorSubject<boolean>(true);

  public readonly EGiftOrderType = EGiftOrderType;

  public readonly regionNameEnglishDefault: TRegionNameEnglish =
    REGION_NAME_ENGLISH_DEFAULT;

  public readonly aliasGift = BOOK_ALIAS_GIFT;

  public readonly aliasRestriction?: TBookAlias[] | undefined;

  public readonly addressDefault: Omit<IAddressShippingData, 'email'> = {
    firstName: 'John',
    lastName: 'Doe',
    line1: 'Kamimuneoka Shiki',
    line2: 'Kamimuneoka District',
    city: 'Seoul',
    postalCode: '353-0001',
    country: 'JP',
    state: 'Tokyo',
    phone: '090-0000-0001',
    // email: 'test@mail.com',
  };

  private readonly formData$ = this._type$.pipe(
    distinctUntilChanged(),
    switchMap((type) => {
      switch (type) {
        case EGiftOrderType.standart:
          return combineLatest([
            this.shipFrom$,
            this.formEmailData$,
            this.formBillingAddressData$,
            this.formPaymentType$,
          ]).pipe(
            map(([shipFrom, emailData, billingAddressData, paymentType]) => ({
              type,
              shipFrom,
              emailData,
              billingAddressData,
              paymentType,
            })),
          );
        case EGiftOrderType.special:
          return combineLatest([
            this.shipFrom$,
            this.formGiftSpecialData$,
            this.formEmailData$,
            this.formBillingAddressData$,
          ]).pipe(
            map(
              ([shipFrom, giftSpecialData, emailData, billingAddressData]) => ({
                type,
                shipFrom,
                giftSpecialData,
                emailData,
                billingAddressData,
              }),
            ),
          );
        // no default
      }

      throw new Error('Invalid gift order type');
    }),
    shareReplay(1),
    takeUntil(this._destroy$),
  );

  private _orderId$ = this._route.queryParamMap.pipe(
    map((queryParamMap) => queryParamMap.get(ROUTE_PATH_PARAM_ORDER_ID)),
    shareReplay(1),
    takeUntil(this._destroy$),
  );

  private _order$ = this._orderId$.pipe(
    // filterNullable(),
    switchMap((orderId) =>
      orderId ? this._firebaseService.getUserGiftOrder$(orderId) : of(null),
    ),
    shareReplay(1),
    takeUntil(this._destroy$),
  );

  private _sub = new Subscriber();

  constructor(
    private _dialogsService: DialogsService,
    private _loaderService: LoaderService,
    private _firebaseService: FirebaseService,
    private _discountService: DiscountService,
    private _stripeService: StripeService,
    private _route: ActivatedRoute,
    private _routingService: RoutingService,
  ) {}

  public ngOnInit(): void {
    this._sub.push(
      // * handle form changes
      this.formData$.subscribe(() => {
        // enable submit button if any of the data is changed
        this.submitButtonDisabled$.next(false);
      }),
      // * when orderId appears in query params, show payment loader until order is paid, has error or konbini window is closed
      this._orderId$.subscribe((orderId) => {
        if (orderId) {
          this._showPaymentLoader();
        }
      }),
      // * when order is updated, check if it has error or is paid
      this._order$
        .pipe(
          // filterNullable(),
          distinctUntilChanged(
            (_a, _b) =>
              !isParamChanged(_a, _b, 'status') &&
              !isParamChanged(_a, _b, 'error'),
          ),
        )
        .subscribe(async (order) => {
          if (!order) {
            this._hidePaymentLoader();
            return;
          }

          if (checkGiftOrderError(order)) {
            /** showing the main error in console for developers */
            log.error(order.error, { order });
            /** showing user readable error */
            alert(
              // TODO: change to japanese
              'An error occurred while processing your order. Please try again.',
            );
            this._hidePaymentLoader();
            await this._routingService.resetQueryParams(this._route);
          }

          if (checkGeneratedGiftOrder(order)) {
            await this._routingService.resetQueryParams(this._route);
            await this._routingService.goToGiftThanks({
              [ROUTE_PATH_PARAM_ORDER_ID]: order.docId,
            });
            // this._hidePaymentLoader(); // no need as gift-thanks-page triggers it itself
          }
        }),
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes.alias?.firstChange) {
      this.submitButtonDisabled$.next(false);
    }

    if (!changes.cover?.firstChange) {
      this.submitButtonDisabled$.next(false);
    }
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
    this._sub.unsubscribe();
  }

  public async onSubmit() {
    const paymentType = await firstValueFrom(this.formPaymentType$);

    // const isValid = this._validateForms();

    // if (!isValid) return;

    if (paymentType === EFormPaymentType.konbini) {
      const confirmed = await firstValueFrom(
        this._dialogsService.showKonbiniConfirmation().afterClosed(),
      );

      if (!confirmed) return;
    }

    // await this.updateOrderData();
    // await this.pay();

    try {
      this._showPaymentLoader();

      const orderId = await this._submit();

      const orderData = await firstValueFrom(
        this._firebaseService.getUserGiftOrder$(orderId),
      );

      // if (orderData) {
      //   sendConversionEvent({
      //     addressBillingData: orderData.addressBillingData,
      //     orderId,
      //   });
      // } else {
      //   log.error('Order data not found for conversion event', { orderId });
      // }
    } catch (err: any) {
      log.error(err);
      alert(
        err?.message ||
          'An error occurred while processing your order. Please try again.',
      );
      this.touchEventEmitter.emit();
      this.submitButtonDisabled$.next(true);
      this._hidePaymentLoader();

      const wrapEl = this.wrapRef?.nativeElement;

      if (wrapEl) {
        scrollToElement(wrapEl);
      } else {
        log.error('!wrapEl');
      }
    }
  }

  private async _submit() {
    const { type, alias, cover, shipping } = this;

    const formData = await firstValueFrom(this.formData$);

    const { shipFrom, emailData, billingAddressData } = formData;

    if (!alias || !emailData || !billingAddressData) {
      const err = getError('missing required field', {
        alias,
        formEmail: emailData,
        formBillingAddress: billingAddressData,
      });
      log.error(err.message);
      throw err;
    }

    const paymentType =
      'paymentType' in formData ? formData.paymentType : EFormPaymentType.card;

    const book: IBookSimpleData = {
      alias,
      cover,
    };

    const discountCampaign = await firstValueFrom(
      this._discountService.activeDiscountCampaign$,
    );
    const shippingDiscount = await firstValueFrom(
      this._discountService.getShippingDiscount$(shipping.id),
    );

    const shippingPrice = shippingDiscount
      ? calculateDiscount(shipping.price, shippingDiscount)
      : shipping.price;

    const giftOrderCreateParamsCommon: IGiftOrderCreateParamsCommon = {
      type,
      book,
      shipFrom,
      addressBillingData: {
        ...billingAddressData,
        email: emailData.email,
      },
      receiveNewsletters: true,
      paymentInformation: getPaymentInformation(
        paymentType,
        { books: [book], products: [] },
        0,
        { discountCampaign },
        undefined,
        shippingPrice,
      ),
      referrer: document.referrer,
      hostname: window.location.hostname,
      origin: window.location.origin,
      userAgent: navigator.userAgent,
      userIp: document.documentElement.dataset.userIp || '',
      fbp: cookie.parse(document.cookie)._fbp || '',
      fbc: cookie.parse(document.cookie)._fbc || '',
    };

    let giftOrderCreateParams: TGiftOrderCreateParams;

    switch (formData.type) {
      case EGiftOrderType.standart: {
        giftOrderCreateParams = {
          ...giftOrderCreateParamsCommon,
          type: EGiftOrderType.standart, // * typescript issue
        };
        break;
      }
      case EGiftOrderType.special: {
        if (!formData.giftSpecialData) throw getError('!giftSpecialData');

        const _book =
          alias === BOOK_ALIAS_GIFT && this.restrictedAliasArr?.length
            ? this.restrictedAliasArr.map<IBookSimpleData>((_alias) => ({
                alias: _alias,
                cover,
              }))
            : book;

        // if (!_book) throw getError('!books');

        const {
          password,
          count,
          expiration,
          campaignCode,
          campaignDescription,
          campaignNumber,
          codeType,
          labelTag,
          statusTag,
          extraTag,
        } = formData.giftSpecialData;

        giftOrderCreateParams = {
          ...giftOrderCreateParamsCommon,
          book: _book,
          type: EGiftOrderType.special, // * typescript issue
          codeType: Number(codeType),
          password,
          count,
          expiration,
          campaign: {
            code: campaignCode,
            description: campaignDescription,
            number: campaignNumber,
          },
          labelTag,
          statusTag,
          extraTag,
        };
        break;
      }

      // no default
    }

    deleteUndefinedFromObject(giftOrderCreateParams);

    const orderId = await this._firebaseService.createUserGiftOrder(
      giftOrderCreateParams,
    );

    await this._routingService.setQueryParam('orderId', orderId, this._route);

    /** wait until order is created */
    const orderData = await firstValueFrom(
      this._firebaseService.getUserGiftOrder$(orderId).pipe(
        filter(
          (
            order,
          ): order is
            | TWithDocId<TGiftOrderCreated>
            | TWithDocId<TGiftOrderCreateParamsError> => {
            const hasCreatedStatus =
              !!order &&
              'status' in order &&
              order.status === EGiftOrderStatus.created;

            const hasError = !!order && 'error' in order && !!order.error;

            return hasCreatedStatus || hasError;
          },
        ),
      ),
    );

    if (checkGiftOrderError(orderData)) {
      const err = getError(orderData.error);
      log.error(err, { orderData });
      throw err;
    }

    switch (orderData.paymentInformation.type) {
      case EFormPaymentType.card: {
        const stripe = await this._stripeService.stripe;
        const { sessionId } = orderData;

        if (!sessionId) throw getError('missing sessionId', { orderData });

        await sendConversionEvent({
          addressBillingData: orderData.addressBillingData,
          orderId,
        });

        const res = await stripe.redirectToCheckout({
          sessionId,
        });

        if ('error' in res) {
          const { error } = res;

          alert(error.message || error);
        }

        break;
      }
      case EFormPaymentType.konbini: {
        throw new Error('type === konbini is not supported anymore');

        // const { firstName, lastName, phone, email } =
        //   orderData.addressBillingData;

        // await this._stripeService.payWithKonbini(
        //   orderId,
        //   getFullName(lastName, firstName),
        //   email,
        //   `${phone.replace(/\D/g, '')}`,
        //   DB_GIFT_PAYMENTS,
        // );

        break;
      }

      // no default
    }

    return orderId;
  }

  private _showPaymentLoader() {
    if (!this._loaderService.loading) {
      this._loaderService.show({ type: ELoaderType.payment, data: {} });
    }
  }

  private _hidePaymentLoader() {
    if (this._loaderService.loading) {
      this._loaderService.hide();
    }
  }
}
